<script>
import {
    preCheckImport,
    submitImport,
    downloadSampleExcel,
    preCheckBulk,
    submitBulk
} from '@/helpers/api/mdm/mdm-command-logs.api';
import axios from 'axios';
import { commonService } from '@/helpers/common.service';

export default {
    name: 'MdmUploadSection',

    props: {
        isRequester:  { type: Boolean, default: true },
        /** Numeric process type: 1=Send Warning, 2=Enable Lost Mode, 3=Disable Lost Mode */
        processType:  { type: Number, default: 1 },
    },

    emits: ['uploaded', 'batch-created'],

    data() {
        return {
            file:         null,
            isLoading:    false,
            isChecking:   false,
            isCommitting: false,
            
            // Validation Results
            hasChecked:   false,
            totalRows:    0,
            validCount:   0,
            invalidCount: 0,
            errors:       [],
            isValid:      false,
            
            resultTab:    'invalid', // 'invalid' | 'valid'
            validRows:    [],
            invalidRows:  [],

            // Final Result
            isCommitted:  false,
            batchId:      null,

            // Templates
            templates:    [],
            selectedTemplateId: null,
            loadingTemplates: false,

            // Individual Entry (Draft List)
            uploadMode:   'bulk', // 'bulk' | 'individual'
            manualSearchQuery: '',
            manualDraftList: [],
            isAddingManual: false,
        };
    },

    computed: {
        processInfo() {
            const map = {
                1: { label: 'Send Warning', color: 'badge-soft-warning', title: 'Send Warning' },
                2: { label: 'Enable Lost Mode',    color: 'badge-soft-danger',  title: 'Enable Lost Mode' },
                3: { label: 'Disable Lost Mode',  color: 'badge-soft-success', title: 'Disable Lost Mode' },
            };
            return map[this.processType] ?? { label: 'Unknown', color: 'badge-soft-secondary', title: 'MDM Process' };
        },
        selectedTemplate() {
            return this.templates.find(t => t.id === this.selectedTemplateId) || null;
        },
        effectiveValidCount() {
            return this.uploadMode === 'bulk' ? this.validCount : this.manualDraftList.length;
        },
        effectiveIsValid() {
            return this.uploadMode === 'bulk' ? this.isValid : (this.manualDraftList.length > 0);
        }
    },

    methods: {
        statusClass(status) {
            const map = {
                0: 'badge-soft-secondary', // Queued
                1: 'badge-soft-info',      // Importing
                2: 'badge-soft-dark',      // (placeholder)
                3: 'badge-soft-warning',   // Waiting Approval
                4: 'badge-soft-success',   // Completed
                5: 'badge-soft-danger',    // Failed
                6: 'badge-soft-dark',      // Rejected
            };
            return map[status] ?? 'badge-soft-secondary';
        },

        handleFileChange(e) {
            this.file = e.target.files[0] ?? null;
            this.resetStatus();
        },

        resetStatus() {
            this.hasChecked   = false;
            this.totalRows    = 0;
            this.validCount   = 0;
            this.invalidCount = 0;
            this.errors       = [];
            this.isValid      = false;
            this.isCommitted  = false;
            this.batchId      = null;
            this.manualDraftList = [];
        },

        async runPreCheck() {
            if (!this.file) return;

            this.isChecking = true;
            this.hasChecked = false;
            this.errors     = [];
            this.validRows  = [];
            this.invalidRows = [];

            try {
                const response = await preCheckImport(this.file, this.processType);
                const info     = response.data || {};
                const summary  = info.summary || {};

                this.totalRows    = summary.total ?? 0;
                this.validCount   = summary.valid ?? 0;
                this.invalidCount = summary.invalid ?? 0;
                this.isValid      = summary.is_valid ?? false;
                
                this.invalidRows = info.invalid_records || [];
                this.validRows   = info.valid_records || [];

                // Default tab focus
                this.resultTab = this.invalidCount > 0 ? 'invalid' : 'valid';
                this.hasChecked = true;

                if (this.isValid) {
                    commonService.swalPrompt(true, 'File is valid. You may proceed to request approval.', 'Validation Success', 2000);
                } else {
                    commonService.swalPrompt(false, `Found ${this.invalidCount} invalid rows. Please fix and re-upload.`, 'Validation Failed');
                }
            } catch (error) {
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, 'Validation Error');
            } finally {
                this.isChecking = false;
            }
        },

        async confirmImport() {
            // Validation check based on mode
            const requiresTemplate = this.processType !== 3;

            if (this.uploadMode === 'bulk') {
                if (!this.file || !this.isValid) return;
                if (requiresTemplate && !this.selectedTemplateId) return;
            } else {
                if (this.manualDraftList.length === 0) return;
                if (requiresTemplate && !this.selectedTemplateId) return;
            }

            const res = await commonService.swalConfirm(
                `Request ${this.processInfo.label} Approval?`, 
                `Are you sure you want to submit all ${this.effectiveValidCount} records for approval?`
            );
            if (!res) return;

            this.isCommitting = true;

            // Handle Individual Submission (Real BE Integration)
            if (this.uploadMode === 'individual') {
                try {
                    const contracts = this.manualDraftList.map(i => i.contract_number);
                    const response = await submitBulk(contracts, this.processType, this.selectedTemplateId);
                    
                    this.isCommitted = true;
                    this.batchId     = response.data?.batch_id;
                    this.isCommitting = false;

                    commonService.swalPrompt(true, response.message || 'Individual entries submitted for approval.', 'Success');
                    this.$emit('batch-created', this.batchId);
                } catch (error) {
                    const msg = error.response?.data?.message ?? error.message;
                    commonService.swalPrompt(false, msg, 'Submission Failed');
                } finally {
                    this.isCommitting = false;
                }
                return;
            }

            try {
                // Bulk Submission (Original BE Integration)
                const response = await submitImport(this.file, this.processType, this.selectedTemplateId);
                this.isCommitted = true;
                this.batchId     = response.data?.batch_id;

                commonService.swalPrompt(true, response.message || 'Import batch created and submitted.', 'Success');
                
                // Reset file input
                this.file = null;
                const input = this.$el.querySelector('#fileUploadInput');
                if (input) input.value = '';

                this.$emit('batch-created', this.batchId);
            } catch (error) {
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, 'Import Failed');
            } finally {
                this.isCommitting = false;
            }
        },

        async fetchTemplates() {
            if (this.processType === 3) {
                this.templates = [];
                this.selectedTemplateId = null;
                return;
            }
            this.loadingTemplates = true;
            
            try {
                axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
                const response = await axios.get(process.env.VUE_APP_API_BASEURL + 'mdm/templates', {
                    params: { 
                        type:   this.processType, 
                        status: 1
                    }
                });
                
                this.templates = response.data?.data || (Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error("Error fetching templates:", error);
            } finally {
                this.loadingTemplates = false;
            }
        },
        
        setActiveResultTab(tab) {
            this.resultTab = tab;
        },

        async downloadTemplate() {
            try {
                const response = await downloadSampleExcel();
                const url      = window.URL.createObjectURL(new Blob([response.data]));
                const link     = document.createElement('a');
                link.href      = url;
                link.setAttribute('download', 'mdm_import_sample.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, 'Download Failed');
            }
        },

        async addIndividualEntry() {
            const query = this.manualSearchQuery.trim().toUpperCase();
            if (!query) return;

            // Avoid duplicates in local draft list
            if (this.manualDraftList.find(i => i.contract_number === query)) {
                commonService.swalPrompt(false, 'This contract is already in your drafting list.', 'Duplicate');
                return;
            }

            this.isAddingManual = true;
            try {
                // Call real Pre-check Bulk API
                const response = await preCheckBulk(query, this.processType);
                
                if (response.status === 'success') {
                    this.manualDraftList.push({
                        contract_id:     response.data.contract_id,
                        contract_number: response.data.contract_number,
                        customer_id:     response.data.customer_id,
                        customer_name:   response.data.customer_name,
                        device_details:  response.data.device_details,
                        ar_details:      response.data.ar_details,
                        status:          response.data.status,
                    });
                    
                    commonService.swalPrompt(true, `Device ${response.data.contract_number} added to draft.`, 'Success', 1500);
                    this.manualSearchQuery = '';
                } else {
                    commonService.swalPrompt(false, response.message || 'Device is not eligible for this action.', 'Invalid Contract');
                }
            } catch (error) {
                // Handling 422 and other error responses
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, 'Invalid Contract');
            } finally {
                this.isAddingManual = false;
            }
        },

        removeIndividualEntry(index) {
            this.manualDraftList.splice(index, 1);
        },
    },

    watch: {
        processType: {
            handler() { 
                this.resetStatus();
                this.fetchTemplates();
            },
            immediate: true
        },
        uploadMode() {
            this.resetStatus();
        }
    },

    mounted() {
        this.fetchTemplates();
    }
};
</script>

<template>
    <div v-if="isRequester" class="p-1">
        
        <div class="d-flex align-items-center gap-2 mb-3">
            <h5 class="mb-0 fw-bold">{{ processInfo.title }} Process</h5>
            <span :class="['badge border text-uppercase', processInfo.color]" style="font-size: 10px;">{{ processInfo.label }}</span>
        </div>

        <!-- Mode Selection -->
        <div class="d-flex align-items-center gap-4 mb-4">
            <div class="form-check form-radio-success">
                <input class="form-check-input" type="radio" v-model="uploadMode" value="bulk" id="modeBulk">
                <label class="form-check-label fs-13 fw-medium" for="modeBulk">
                    Bulk Excel Upload
                </label>
            </div>
            <div class="form-check form-radio-success">
                <input class="form-check-input" type="radio" v-model="uploadMode" value="individual" id="modeIndividual">
                <label class="form-check-label fs-13 fw-medium" for="modeIndividual">
                    Individual Entry
                </label>
            </div>
        </div>

        <!-- Business Rules -->
        <div class="card border-0 shadow-none mb-4" :class="[processType === 1 ? 'bg-soft-warning' : (processType === 2 ? 'bg-soft-danger' : 'bg-soft-success')]">
            <div class="card-body py-2 px-3">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <i :class="[processType === 1 ? 'ri-error-warning-line text-warning' : (processType === 2 ? 'ri-shield-flash-line text-danger' : 'ri-checkbox-circle-line text-success'), 'fs-16']"></i>
                    <h6 class="fs-13 fw-semibold mb-0" :class="processType === 1 ? 'text-warning' : (processType === 2 ? 'text-danger' : 'text-success')">Business Rules &amp; Validations</h6>
                </div>
                <ul class="list-unstyled mb-0 fs-12 d-flex flex-column gap-1 text-muted ms-4">
                    <template v-if="processType === 1"> <!-- Warning -->
                        <li class="d-flex align-items-center gap-2">
                            <i class="ri-checkbox-circle-fill text-success fs-14"></i>
                            <span>Warnings can be sent multiple times. Fail if device is in <strong>LOST MODE</strong>.</span>
                        </li>
                    </template>
                    <template v-else-if="processType === 2"> <!-- Enable Lost Mode -->
                        <li class="d-flex align-items-center gap-2">
                            <i class="ri-checkbox-circle-fill text-success fs-14"></i>
                            <span>Lost Mode can be enabled once. Fail if device is already in <strong>LOST MODE</strong>.</span>
                        </li>
                    </template>
                    <template v-else-if="processType === 3"> <!-- Disable Lost Mode -->
                        <li class="d-flex align-items-center gap-2">
                            <i class="ri-checkbox-circle-fill text-success fs-14"></i>
                            <span>Disable Lost Mode can only be sent once. Fail if device is <strong>NOT</strong> in <strong>LOST MODE</strong>.</span>
                        </li>
                    </template>
                </ul>
            </div>
        </div>

        <!-- Mode 1: Bulk Upload & Pre-check -->
        <template v-if="uploadMode === 'bulk'">
            <div v-if="!isCommitted" class="row g-3 align-items-end mb-2">
                <div class="col-md-9">
                    <label class="form-label text-muted fs-13 mb-0">
                        Select Source File (.xlsx, .xls, .csv)
                    </label>
                    <input
                        type="file"
                        id="fileUploadInput"
                        class="form-control"
                        accept=".csv,.xlsx,.xls"
                        @change="handleFileChange"
                    >
                </div>
                <div class="col-md-3">
                    <button
                        class="btn btn-primary w-100"
                        @click="runPreCheck"
                        :disabled="!file || isChecking || isCommitting"
                    >
                        <span v-if="isChecking">
                            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                            Checking...
                        </span>
                        <span v-else>
                            <i class="ri-search-eye-line me-1 align-bottom"></i> Pre-check File
                        </span>
                    </button>
                </div>
            </div>

            <p v-if="!isCommitted" class="text-muted fs-12 mb-4">
                <i class="ri-information-line me-1 align-bottom"></i>
                Please refer to the
                <a href="javascript:void(0);" @click="downloadTemplate" class="text-primary fw-medium text-decoration-underline">
                    sample excel
                </a>
                before uploading.
            </p>
        </template>

        <!-- Mode 2: Individual Entry (Search & Add) -->
        <template v-else-if="uploadMode === 'individual'">
            <div v-if="!isCommitted" class="card border border-dashed shadow-none mb-4 bg-light-subtle">
                <div class="card-body p-3">
                    <label class="form-label text-muted fs-11 text-uppercase fw-bold mb-2">Add Individual Record</label>
                    <div class="row g-2">
                        <div class="col-md-9">
                            <div class="input-group">
                                <span class="input-group-text bg-white border-light-subtle">
                                    <i class="ri-search-line text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    v-model="manualSearchQuery"
                                    class="form-control border-light-subtle px-2"
                                    placeholder="Enter Contract Number..."
                                    @keyup.enter="addIndividualEntry"
                                >
                            </div>
                        </div>
                        <div class="col-md-3">
                            <button 
                                class="btn btn-soft-primary w-100" 
                                @click="addIndividualEntry"
                                :disabled="!manualSearchQuery.trim() || isAddingManual"
                            >
                                <i class="ri-add-line align-bottom me-1"></i> 
                                {{ isAddingManual ? 'Searching...' : 'Add device' }}
                            </button>
                        </div>
                    </div>
                    <div class="mt-2 text-muted fs-11">
                        <i class="ri-information-line me-1 align-bottom"></i>
                        Find specific device by <strong>contract_number</strong> to add to the submission list.
                    </div>
                </div>
            </div>
            
            <!-- Manual Draft List Table -->
            <div v-if="manualDraftList.length > 0 && !isCommitted" class="card border mb-4 animated fadeIn">
                <div class="card-header bg-light py-2">
                    <h6 class="card-title mb-0 fs-13 d-flex align-items-center">
                        <i class="ri-list-check-2 me-1 align-bottom"></i> 
                        Individual Devices to Process
                        <span class="badge badge-soft-primary ms-2">{{ manualDraftList.length }}</span>
                    </h6>
                </div>
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table table-hover table-nowrap align-middle mb-0 table-sm fs-12">
                            <thead class="table-light">
                                <tr>
                                    <th class="ps-3">Contract No.</th>
                                    <th>Customer Name</th>
                                    <th>Device Details</th>
                                    <th>Outstanding / Overdue</th>
                                    <th>Unpaid Invoices</th>
                                    <th>Status</th>
                                    <th class="text-end pe-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in manualDraftList" :key="'manual-'+idx">
                                    <td class="ps-3">
                                        <router-link target="_blank" :to="`/contracts/customer-contract-details/${item.contract_id}`" class="fw-medium link-secondary fs-12">
                                            {{ item.contract_number }}
                                        </router-link>
                                    </td>
                                    <td>
                                        <router-link target="_blank" :to="`/customer/customer-details/${item.customer_id}`" class="fw-medium link-secondary">{{ item.customer_name }}</router-link>
                                    </td>
                                    <td class="text-muted fs-11">{{ item.device_details }}</td>
                                    <td>
                                        <div v-if="item.ar_details" class="d-flex flex-column lh-1">
                                            <span class="fs-11 fw-bold text-danger">RM {{ item.ar_details.total_outstanding }}</span>
                                            <span class="fs-10 text-muted mt-1" v-if="item.ar_details.total_overdue">Overdue: RM {{ item.ar_details.total_overdue }}</span>
                                        </div>
                                        <span v-else class="text-muted fs-12">---</span>
                                    </td>
                                    <td>
                                        <span v-if="item.ar_details" class="fs-11 text-dark">{{ item.ar_details.unpaid_invoice_count }}</span>
                                        <span v-else class="text-muted fs-12">---</span>
                                    </td>
                                    <td>
                                        <span class="badge badge-soft-success fs-10">{{ item.status || 'Valid' }}</span>
                                    </td>
                                    <td class="text-end pe-3">
                                        <button class="btn btn-soft-danger btn-sm btn-icon waves-effect" @click="removeIndividualEntry(idx)">
                                            <i class="ri-delete-bin-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            
            <div v-else-if="!isCommitted" class="alert alert-soft-secondary border-dashed text-center py-4 mb-4">
                <i class="ri-user-search-line fs-24 text-muted mb-2 d-block"></i>
                <h6 class="text-muted mb-0">No individual devices added yet.</h6>
                <p class="text-muted fs-12 mt-1 mb-0">Search and add devices using the form above.</p>
            </div>
        </template>

        <!-- Mode 1: Bulk Results (Only shown if mode is bulk and file checked) -->
        <div v-if="uploadMode === 'bulk' && hasChecked && !isCommitted" class="mb-4 animated fadeIn">
            <div class="row g-2 mb-4">
                <div class="col-4">
                    <div class="card shadow-none border border-light-subtle bg-light mb-0 h-100">
                        <div class="card-body p-2 text-center">
                            <div class="text-muted fs-11 text-uppercase fw-bold">Total Rows</div>
                            <h5 class="mb-0 fs-16">{{ totalRows }}</h5>
                        </div>
                    </div>
                </div>
                <div class="col-4">
                    <div class="card shadow-none border border-success-subtle bg-success-subtle mb-0 h-100">
                        <div class="card-body p-2 text-center">
                            <div class="text-success fs-11 text-uppercase fw-bold">Valid</div>
                            <h5 class="text-success mb-0 fs-16">{{ validCount }}</h5>
                        </div>
                    </div>
                </div>
                <div class="col-4">
                    <div class="card shadow-none border border-danger-subtle bg-danger-subtle mb-0 h-100">
                        <div class="card-body p-2 text-center">
                            <div class="text-danger fs-11 text-uppercase fw-bold">Invalid</div>
                            <h5 class="text-danger mb-0 fs-16">{{ invalidCount }}</h5>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabbed Details -->
            <div class="card border mb-0">
                <div class="card-header p-0">
                    <ul class="nav nav-tabs-custom nav-success border-bottom-0" role="tablist">
                        <li class="nav-item">
                            <a class="nav-link py-3" :class="{ active: resultTab === 'invalid' }" @click.prevent="setActiveResultTab('invalid')" href="#">
                                <i class="ri-error-warning-line me-1 align-bottom"></i> 
                                Invalid Records ({{ invalidCount }})
                            </a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link py-3" :class="{ active: resultTab === 'valid' }" @click.prevent="setActiveResultTab('valid')" href="#">
                                <i class="ri-checkbox-circle-line me-1 align-bottom"></i> 
                                Valid Listing ({{ validCount }})
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="card-body p-0">
                    <div class="tab-content border-0">
                        <!-- Invalid Tab -->
                        <div v-show="resultTab === 'invalid'" class="tab-pane show active animated fadeIn">
                            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                                <table class="table table-striped table-nowrap align-middle mb-0 table-sm fs-12">
                                    <thead class="table-light sticky-top" style="top: 0; z-index: 1;">
                                        <tr>
                                            <th class="ps-3" style="width: 80px;">Row #</th>
                                            <th>Contract No.</th>
                                            <th>Error Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, idx) in invalidRows" :key="'inv-'+idx">
                                            <td class="ps-3 fw-bold">{{ row.row }}</td>
                                            <td class="text-primary">{{ row.contract_number }}</td>
                                            <td class="text-danger">
                                                <i class="ri-close-circle-fill me-1"></i> {{ row.reason }}
                                            </td>
                                        </tr>
                                        <tr v-if="invalidRows.length === 0">
                                            <td colspan="3" class="text-center py-4 text-muted">No invalid records found.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <!-- Valid Tab -->
                        <div v-show="resultTab === 'valid'" class="tab-pane show active animated fadeIn">
                            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                                <table class="table table-hover table-nowrap align-middle mb-0 table-sm fs-12">
                                    <thead class="table-light sticky-top" style="top: 0; z-index: 1;">
                                        <tr>
                                            <th class="ps-3">Contract No.</th>
                                            <th>Customer Name</th>
                                            <th>Device Details</th>
                                            <th>Outstanding / Overdue</th>
                                            <th>Unpaid Invoices</th>
                                            <th class="text-end pe-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, idx) in validRows" :key="'val-'+idx">
                                            <td class="ps-3">
                                                <a target="_blank" :href="`/contracts/customer-contract-details/${row.contract_id}`" class="text-primary fw-medium fs-12">
                                                    {{ row.contract_number }}
                                                </a>
                                            </td>
                                            <td>
                                                <a target="_blank" :href="`/customer/customer-details/${row.customer_id}`" class="text-primary">{{ row.customer_name }}</a>
                                            </td>
                                            <td class="text-muted fs-11">{{ row.device_details }}</td>
                                            <td>
                                                <div v-if="row.ar_details" class="d-flex flex-column lh-1">
                                                    <span class="fs-11 fw-bold text-danger">RM {{ row.ar_details.total_outstanding }}</span>
                                                    <span class="fs-10 text-muted mt-1" v-if="row.ar_details.total_overdue">Overdue: RM {{ row.ar_details.total_overdue }}</span>
                                                </div>
                                                <span v-else class="text-muted fs-12">---</span>
                                            </td>
                                            <td>
                                                <span v-if="row.ar_details" class="fs-11 text-dark">{{ row.ar_details.unpaid_invoice_count }}</span>
                                                <span v-else class="text-muted fs-12">---</span>
                                            </td>
                                            <td class="text-end pe-3">
                                                <span class="badge badge-soft-success fs-10">{{ row.status || 'Valid' }}</span>
                                            </td>
                                        </tr>
                                        <tr v-if="validRows.length === 0">
                                            <td colspan="6" class="text-center py-4 text-muted">No valid records found.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!isValid" class="alert alert-warning border-dashed d-flex align-items-center mb-0 mt-3">
                <i class="ri-information-fill fs-18 me-2"></i>
                <div class="flex-grow-1 fs-12">
                    Found <strong>{{ invalidCount }} invalid rows</strong>. You must fix the records in your Excel file and re-upload before you can request approval.
                </div>
            </div>
        </div>

        <!-- Common Steps (Step 2 & Submit) -->
        <template v-if="effectiveIsValid && effectiveValidCount > 0 && !isCommitted">
            <!-- Step 2: Template Selection (Only for Warning and Enable Lost Mode) -->
            <div v-if="processType !== 3" class="card border border-primary-subtle shadow-sm mb-4 animated fadeIn">
                <div class="card-header bg-soft-primary py-2">
                    <h6 class="card-title mb-0 fs-13 text-primary"><i class="ri-chat-voice-line me-1 align-bottom"></i> Step 2: Select Message Template</h6>
                </div>
                <div class="card-body">
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label class="form-label fs-12 text-muted">Action Template <span class="text-danger">*</span></label>
                            
                            <div v-if="loadingTemplates" class="py-1">
                                <div class="spinner-border spinner-border-sm text-primary me-2"></div>
                                <span class="fs-12 text-muted">Loading available templates...</span>
                            </div>
                            <select v-else v-model="selectedTemplateId" class="form-select fs-13" :disabled="isCommitting">
                                <option :value="null">-- Select Template --</option>
                                <option v-for="t in templates" :key="t.id" :value="t.id">
                                    {{ t.name || t.message_title }}
                                </option>
                            </select>
                            <div v-if="templates.length === 0 && !loadingTemplates" class="mt-1 fs-11 text-danger">
                                <i class="ri-error-warning-line me-1"></i> No active templates found for {{ processInfo.label }}. Please create one in Settings.
                            </div>
                        </div>
                        <div class="col-md-6 border-start" v-if="selectedTemplate">
                            <label class="form-label fs-12 text-muted">Message Preview</label>
                            <div class="p-2 bg-light rounded border fs-12 overflow-auto" style="max-height: 120px;">
                                <div class="fw-bold text-dark mb-1">{{ selectedTemplate.name || selectedTemplate.message_title }}</div>
                                <p class="mb-0 text-muted">{{ selectedTemplate.message_body }}</p>
                                <div v-if="selectedTemplate.footnote" class="mt-2 pt-2 border-top italic text-muted small">
                                    {{ selectedTemplate.footnote }}
                                </div>
                                <div v-if="selectedTemplate.phone_number" class="mt-1 fw-medium text-dark small">
                                    <i class="ri-phone-line me-1"></i> {{ selectedTemplate.phone_number }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Final Process Action -->
            <div v-if="uploadMode === 'individual' || (uploadMode === 'bulk' && invalidCount === 0)" class="alert alert-soft-success border-dashed d-flex align-items-center mb-0">
                <div class="flex-grow-1">
                    <h6 class="alert-heading fw-bold mb-1">Ready for Approval Request!</h6>
                    <p class="fs-12 mb-0" v-if="processType !== 3">All {{ effectiveValidCount }} records passed. Please select a message template before submitting for approval.</p>
                    <p class="fs-12 mb-0" v-else>All {{ effectiveValidCount }} records passed. No template is required for disabling lost mode.</p>
                </div>
                <div class="flex-shrink-0 ms-2">
                    <button 
                         class="btn btn-primary px-4 shadow-sm" 
                        @click="confirmImport"
                        :disabled="isCommitting || (processType !== 3 && !selectedTemplateId)"
                    >
                        <span v-if="isCommitting">
                            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                            Submitting...
                        </span>
                        <span v-else>
                            <i class="ri-send-plane-2-line me-1 align-bottom"></i> Request Batch Approval
                        </span>
                    </button>
                </div>
            </div>
        </template>

        <!-- Success Committed State -->
        <div v-if="isCommitted" class="text-center py-4 animated fadeIn">
            <div class="avatar-md mx-auto mb-3">
                <div class="avatar-title bg-soft-success text-success display-6 rounded-circle">
                    <i class="ri-checkbox-circle-fill"></i>
                </div>
            </div>
            <h5 class="fw-bold">Import Batch Created!</h5>
            <p class="text-muted fs-14 mb-4">
                Batch ID: <strong>#{{ batchId }}</strong><br>
                The import is being processed in the background. You can track the progress in the <strong>Import History</strong> tab.
            </p>
            <div class="d-flex justify-content-center gap-2">
                <button class="btn btn-primary" @click="resetStatus">
                    <i class="ri-upload-2-line me-1 align-bottom"></i> Import Another File
                </button>
                <button class="btn btn-soft-info" @click="$emit('view-history')">
                    <i class="ri-history-line me-1 align-bottom"></i> View Import History
                </button>
            </div>
        </div>

    </div>

    <!-- Restricted View -->
    <div v-else class="text-center py-5">
        <h5 class="text-muted">Access Restricted</h5>
    </div>
</template>
